import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Instagram } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { useLanguage } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";
import { Link } from "react-router-dom";

const ContactPage = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-muted/30 py-12 md:py-16">
        <div className="container">
          <BackButton className="mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{getTranslation("contactUs", language)}</h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {getTranslation("contactDescription", language)}
          </p>
        </div>
      </div>
      
      {/* Contact Content */}
      <div className="container py-12">
        <div className="w-full">
          <div className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                {/* Contact Details */}
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-2xl font-semibold mb-6 text-center">{getTranslation("contactDetails", language)}</h2>
                  
                  <div className="space-y-8 mb-12">
                    <div className="flex items-start gap-4 bg-muted/30 p-6 rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">{getTranslation("address", language)}</h3>
                        <p className="text-muted-foreground">ul. Stary Rynek 61/2</p>
                        <p className="text-muted-foreground">61-772 Poznań</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 bg-muted/30 p-6 rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">Email</h3>
                        <a 
                          href="mailto:kontakt1group.pl@gmail.com" 
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          kontakt1group.pl@gmail.com
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 bg-muted/30 p-6 rounded-lg">
                      <div className="bg-primary/10 p-3 rounded-full">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">{getTranslation("phone", language)}</h3>
                        <a 
                          href="tel:+48886695593" 
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          +48 886 695 593
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 bg-muted/30 p-6 rounded-lg">
                      <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-3 rounded-full">
                        <Instagram className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium mb-1">Instagram</h3>
                        <a 
                          href="https://www.instagram.com/1group.pl?igsh=bjNhaXM4em1rNmtu" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          @1group.pl
                        </a>
                      </div>
                    </div>
                    
                    <div className="bg-muted/30 p-6 rounded-lg">
                      <h3 className="font-medium mb-2">{getTranslation("openingHours", language)}</h3>
                      <p className="text-muted-foreground">{getTranslation("workingDays", language)}</p>
                      <p className="text-muted-foreground">{getTranslation("weekends", language)}</p>
                    </div>
                  </div>
                  
                  {/* Quick Contact Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button asChild size="lg" className="flex items-center gap-2">
                      <a href="tel:+48886695593">
                        <Phone className="h-5 w-5" />
                        {getTranslation("quickContact", language)}
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="flex items-center gap-2">
                      <a href="mailto:kontakt1group.pl@gmail.com">
                        <Mail className="h-5 w-5" />
                        {getTranslation("emailContact", language)}
                      </a>
                    </Button>
                    <Button asChild variant="default" size="lg" className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                      <a href="https://www.instagram.com/1group.pl?igsh=bjNhaXM4em1rNmtu" target="_blank" rel="noopener noreferrer">
                        <Instagram className="h-5 w-5" />
                        Instagram
                      </a>
                    </Button>
                  </div>
                  
                  {/* Map */}
                  <div className="mt-12">
                    <h3 className="font-medium mb-3 text-center text-xl">{getTranslation("ourLocation", language)}</h3>
                    <div className="rounded-lg overflow-hidden border border-border">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2433.6476698681275!2d16.93229247660153!3d52.40888904530977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47045b3a250db809%3A0x8d9b0c7c5b484867!2sStary%20Rynek%2061%2C%2061-772%20Pozna%C5%84%2C%20Poland!5e0!3m2!1sen!2sus!4v1695219458209!5m2!1sen!2sus"
                        className="w-full h-64 border-0"
                        allowFullScreen={true}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="1Group location map"
                        aria-label="Google Maps showing 1Group office location"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default ContactPage;